//Source: https://claude.ai/chat/c003785e-1131-4bce-9a6b-da998987563c
/**
 * Elegant Tooltip Plugin
 * A lightweight, zero-dependency tooltip system for web applications.
 * Intercepts elements with [title] attributes and renders a polished tooltip.
 *
 * Usage (Vanilla JS):
 *   import { createTooltip } from './tooltip.js';
 *   createTooltip();
 *
 * Usage (Vue 3 plugin):
 *   import TooltipPlugin from './tooltip.js';
 *   app.use(TooltipPlugin);
 *
 * Usage (Vue 2 plugin):
 *   import TooltipPlugin from './tooltip.js';
 *   Vue.use(TooltipPlugin);
 */

const TOOLTIP_ID = '__elegant-tooltip__';
const OFFSET = 12; // px gap between cursor/element and tooltip


//TODO: Keeping for short term reference
//Original: --tt-bg: rgba(15, 15, 20, 0.92);


const STYLES = `
  #${TOOLTIP_ID} {
    --tt-bg: rgba(7, 48, 160, 0.92);
    --tt-border: rgba(255, 255, 255, 0.08);
    --tt-text: #f0eee8;
    --tt-shadow: 0 8px 32px rgba(0, 0, 0, 0.28), 0 1.5px 6px rgba(0,0,0,0.18);
    --tt-radius: 7px;
    --tt-font: 'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif;
    --tt-size: 1em;
    --tt-weight: 100;
    --tt-padding: 7px 12px;
    --tt-duration: 140ms;
    --tt-blur: blur(12px);

    position: fixed;
    z-index: 1000000;
    pointer-events: none;
    display: block;

    max-width: 400px;
    padding: var(--tt-padding);
    border-radius: var(--tt-radius);

    background: var(--tt-bg);
    -webkit-backdrop-filter: var(--tt-blur);
    backdrop-filter: var(--tt-blur);
    border: 1px solid var(--tt-border);
    box-shadow: var(--tt-shadow);

    font-family: var(--tt-font);
    font-size: var(--tt-size);
    font-weight: var(--tt-weight);
    color: var(--tt-text);
    line-height: 1.5;
    letter-spacing: 0.01em;
    white-space: pre-wrap;
    word-break: break-word;

    opacity: 0;
    transform: translateY(4px) scale(0.98);
    transition:
      opacity var(--tt-duration) ease,
      transform var(--tt-duration) ease;
    will-change: transform, opacity;
  }

  #${TOOLTIP_ID}.visible {
    opacity: 1;
    transform: translateY(0) scale(1);
  }

  /* Subtle top accent line */
  #${TOOLTIP_ID}::before {
    content: '';
    position: absolute;
    top: 0; left: 12px; right: 12px;
    height: 1px;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent);
    border-radius: 1px;
  }
`;

function injectStyles()
{
	if (document.getElementById(`${TOOLTIP_ID}-styles`)) return;
	const style = document.createElement('style');
	style.id = `${TOOLTIP_ID}-styles`;
	style.textContent = STYLES;
	document.head.appendChild(style);
}

function getOrCreateTooltipEl()
{
	let el = document.getElementById(TOOLTIP_ID);
	if (!el)
	{
		el = document.createElement('div');
		el.id = TOOLTIP_ID;
		el.setAttribute('role', 'tooltip');
		el.setAttribute('aria-hidden', 'true');
		document.body.appendChild(el);
	}
	return el;
}

function positionTooltip(el, mouseX, mouseY)
{
	const pad = 8; // min distance from viewport edge
	const vpW = window.innerWidth;
	const vpH = window.innerHeight;

	// Measure tooltip size
	el.style.visibility = 'hidden';
	el.style.display = 'block';
	const rect = el.getBoundingClientRect();
	el.style.visibility = '';

	const ttW = rect.width;
	const ttH = rect.height;

	// Default: below-right of cursor
	let x = mouseX + OFFSET;
	let y = mouseY + OFFSET;

	// Flip horizontal if overflowing right
	if (x + ttW + pad > vpW)
	{
		x = mouseX - ttW - OFFSET;
	}
	// Clamp left
	if (x < pad) x = pad;

	// Flip vertical if overflowing bottom
	if (y + ttH + pad > vpH)
	{
		y = mouseY - ttH - OFFSET;
	}
	// Clamp top
	if (y < pad) y = pad;

	el.style.left = `${x}px`;
	el.style.top = `${y}px`;
}

function getTooltipHost(target)
{
	if (!target) return document.body;
	// Native modal dialogs render in the top layer.
	// Rendering the tooltip inside the active dialog keeps it above dialog/backdrop.
	return target.closest('dialog[open]') || document.body;
}

function ensureTooltipHost(tooltip, target)
{
	const host = getTooltipHost(target);
	if (tooltip.parentElement !== host)
	{
		host.appendChild(tooltip);
	}
}

export function createTooltip()
{
	injectStyles();
	const tooltip = getOrCreateTooltipEl();

	let currentTarget = null; // element currently being hovered
	let rafId = null;         // pending rAF id

	function cancelPendingRaf()
	{
		if (rafId !== null)
		{
			cancelAnimationFrame(rafId);
			rafId = null;
		}
	}

	function show(target, mouseX, mouseY)
	{
		const text = target.dataset.tooltipText;
		if (!text) return;
		ensureTooltipHost(tooltip, target);
		tooltip.textContent = text;
		positionTooltip(tooltip, mouseX, mouseY);
		tooltip.classList.add('visible');
	}

	function hide()
	{
		cancelPendingRaf();
		currentTarget = null;
		tooltip.classList.remove('visible');
	}

	function onMouseOver(e)
	{
		const target = e.target.closest('[title], [data-tooltip-text]');

		if (target && target.classList.contains("no-tooltip"))
		{
			if (currentTarget) hide();
			return;
		}

		// Other special cases
		if (target && target.classList.contains("vs__deselect"))
		{
			if (currentTarget) hide();
			return;
		}

		if (!target)
		{
			if (currentTarget) hide();
			return;
		}

		// Transfer title → data attr to suppress the native browser tooltip
		if (target.hasAttribute('title'))
		{
			target.dataset.tooltipText = target.getAttribute('title');
			target.removeAttribute('title');
		}

		// Already showing for this target — nothing to do
		if (currentTarget === target) return;

		// New target: hide current, then show on next frame
		hide();
		currentTarget = target;

		rafId = requestAnimationFrame(() =>
		{
			rafId = null;
			if (currentTarget === target) show(target, e.clientX, e.clientY);
		});
	}

	function onMouseMove(e)
	{
		if (!currentTarget) return;
		const target = currentTarget; // snapshot — currentTarget may change before frame fires
		cancelPendingRaf();
		rafId = requestAnimationFrame(() =>
		{
			rafId = null;
			if (currentTarget === target) show(target, e.clientX, e.clientY);
		});
	}

	function onMouseOut(e)
	{
		if (!currentTarget) return;
		const related = e.relatedTarget;
		if (related && currentTarget.contains(related)) return;
		hide();
	}

	function onScroll()
	{
		hide();
	}

	document.addEventListener('mouseover', onMouseOver, { passive: true });
	document.addEventListener('mousemove', onMouseMove, { passive: true });
	document.addEventListener('mouseout', onMouseOut, { passive: true });
	document.addEventListener('scroll', onScroll, { passive: true, capture: true });

	// Return a cleanup function
	return function destroy()
	{
		document.removeEventListener('mouseover', onMouseOver);
		document.removeEventListener('mousemove', onMouseMove);
		document.removeEventListener('mouseout', onMouseOut);
		document.removeEventListener('scroll', onScroll, { capture: true });
		tooltip.remove();
		document.getElementById(`${TOOLTIP_ID}-styles`)?.remove();
	};
}

/** Vue plugin (works with both Vue 2 and Vue 3) */
const TooltipPlugin = {
	install()
	{
		createTooltip();
	},
};

export default TooltipPlugin;